import { c as _c } from "react/compiler-runtime";
import { getSchemeSafeUrl } from '@onefootprint/core';
import type { CompletePage, CtaTarget } from '@onefootprint/request-types';
import { MarkdownText, PageShell } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import type { AutoAdvance } from '@/idv2/features/idv-to-idv2/idv2-provider';
import { useAutoAdvance } from './use-auto-advance';
type CompletionScreenProps = {
  page: CompletePage;
  validationToken: string;
  /** What the host does once the page's countdown ends. Without it no countdown runs. */
  autoAdvance?: AutoAdvance;
  /** Holds the countdown at its first value, for stories and previews. */
  paused?: boolean;
};

// The onboarding is already complete when this screen renders, so the link is a plain anchor
// with nothing at stake. A same-tab destination receives the validation_token (mirroring the
// hosted redirect_url contract); a new-tab link opens the URL verbatim. An unresolved
// `{{ data.* }}` detokenizes to "", so an empty label or scheme-unsafe URL renders no button.
const getCtaHref = (cta: {
  url: string;
  target: CtaTarget;
} | undefined, validationToken: string) => {
  if (!cta) {
    return null;
  }
  const safeUrl = getSchemeSafeUrl(cta.url);
  if (!safeUrl) {
    return null;
  }
  if (cta.target === 'new_tab') {
    return safeUrl;
  }
  const url = new URL(safeUrl);
  url.searchParams.set('validation_token', validationToken);
  return url.toString();
};

// `_self` here is the SDK's own iframe, which the embedder's frame-src and the destination's
// X-Frame-Options both refuse to load; hosted is unframed, so `_top` is the same tab there.
const getCtaTarget = (target: CtaTarget) => target === 'new_tab' ? '_blank' as const : '_top' as const;
export const CompletionScreen = t0 => {
  const $ = _c(14);
  const {
    page,
    validationToken,
    autoAdvance,
    paused
  } = t0;
  const {
    t
  } = useTranslation("idv2");
  const href = getCtaHref(page.cta, validationToken);
  const secondaryHref = getCtaHref(page.secondaryCta, validationToken);
  const primary = page.cta?.label && href ? {
    label: page.cta.label,
    href,
    target: page.cta.target
  } : undefined;
  const secondary = page.secondaryCta?.label && secondaryHref ? {
    label: page.secondaryCta.label,
    href: secondaryHref,
    target: page.secondaryCta.target
  } : undefined;
  const followedHref = [primary, secondary].find(_temp)?.href;
  const seconds = page.autoAdvanceAfterSecs;
  const enabled = !!seconds && !!autoAdvance && (autoAdvance.mode === "close" || !!followedHref);
  const {
    count,
    stop
  } = useAutoAdvance({
    seconds: seconds ?? 0,
    enabled,
    paused,
    onDone: () => {
      if (autoAdvance?.mode === "close") {
        autoAdvance.onDone();
      } else {
        if (autoAdvance && followedHref) {
          autoAdvance.onDone(followedHref);
        }
      }
    }
  });
  const onClick = enabled ? {
    onClick: stop
  } : undefined;
  const cta = primary ? {
    text: primary.label,
    type: "link" as const,
    href: primary.href,
    target: getCtaTarget(primary.target),
    ...onClick
  } : undefined;
  const secondaryCta = secondary ? {
    text: secondary.label,
    type: "link" as const,
    href: secondary.href,
    target: getCtaTarget(secondary.target),
    variant: page.secondaryCta?.style === "link" ? "link" as const : "secondary" as const,
    ...onClick
  } : undefined;
  let t1;
  if ($[0] !== autoAdvance || $[1] !== count || $[2] !== enabled || $[3] !== t) {
    t1 = autoAdvance && enabled ? <span role="timer">{autoAdvance.mode === "navigate" ? t(_temp2, {
        seconds: count
      }) : t(_temp3, {
        seconds: count
      })}</span> : undefined;
    $[0] = autoAdvance;
    $[1] = count;
    $[2] = enabled;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const caption = t1;
  let t2;
  if ($[5] !== page.content) {
    t2 = page.content && <MarkdownText className="text-body-2 text-primary">{page.content}</MarkdownText>;
    $[5] = page.content;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== caption || $[8] !== cta || $[9] !== page.header || $[10] !== page.subheader || $[11] !== secondaryCta || $[12] !== t2) {
    t3 = <PageShell title={page.header} subtitle={page.subheader} cta={cta} secondaryCta={secondaryCta} caption={caption}>{t2}</PageShell>;
    $[7] = caption;
    $[8] = cta;
    $[9] = page.header;
    $[10] = page.subheader;
    $[11] = secondaryCta;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
function _temp(link) {
  return link?.target === "same_tab";
}
function _temp2($) {
  return $.complete.autoAdvance.navigate;
}
function _temp3($_0) {
  return $_0.complete.autoAdvance.close;
}